import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Color, Grid, Shadow, Type } from '../../../app/StyleGuide';
import { Card } from '../../../components/Layout';
import { CurriculumPricingEntity } from '../../curriculum/models/curriculumPricing';
import { SelectedCurriculum } from '../SwitchCurriculum';
import { CurriculumWithSubscription } from '../hooks/useFetchSubscriptions';
import { SmallTag } from './SmallTag';

const getMinistrySizeOptions = (ageGroup?: CurriculumPricingEntity) => {
  const subscriptions = ageGroup?.subscription ?? [];
  const uniqueSizes = Array.from(new Set(subscriptions.map(s => s.size).filter(Boolean)));

  const getMinValue = (size: string) => {
    const match = size.match(/^\d+/);
    return match ? parseInt(match[0], 10) : Number.MAX_SAFE_INTEGER;
  };

  uniqueSizes.sort((a, b) => getMinValue(a) - getMinValue(b));

  return uniqueSizes.map(size => ({ label: size, value: size, ageGroup: ageGroup }));
};

const OptionsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${Grid._5};
  padding: ${Grid._5} 0;
`;

const Option = styled(Card)<{ isSelected: boolean }>`
  position: relative;
  padding: ${Grid._5};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid ${props => (props.isSelected ? Color.Blue._50 : 'transparent')};

  &:hover {
    transform: scale(1.05);
    box-shadow: ${Shadow.medium};
  }
`;

const OptionTitle = styled.h2`
  font-size: ${Type.Scale._6};
  margin: 0;
  text-align: center;
  color: ${Type.Color.dark};
`;

const OptionSubtitle = styled.p`
  font-size: ${Type.Scale._4};
  text-align: center;
  color: ${Color.Gray._70};
  margin: ${Grid._3} 0 0;
`;

const CurrentTag = styled(SmallTag)`
  position: absolute;
  top: -${Grid._4};
  right: ${Grid._3};
`;

type SelectMinistrySizeProps = {
  selectedCurriculum: SelectedCurriculum;
  onSelect: (curriculumEntity: CurriculumPricingEntity) => void;
  currentMinistrySize?: string;
};

const SelectMinistrySize: React.FC<SelectMinistrySizeProps> = ({
  selectedCurriculum,
  onSelect,
  currentMinistrySize,
}) => {
  const [selectedOption, setSelectedOption] = useState<string>();
  const [ministrySizeOptions, setMinistrySizeOptions] = useState<
    { label: string; value: string; ageGroup?: CurriculumPricingEntity }[]
  >([]);

  const handleSelect = ({ value, ageGroup }: { label: string; value: string; ageGroup?: CurriculumPricingEntity }) => {
    // Filter out ministry sizes
    const transformedCurriculumEntity = {
      ...ageGroup,
      subscription: ageGroup?.subscription.filter(s => s.size === value),
    };

    setSelectedOption(value);
    onSelect(transformedCurriculumEntity as CurriculumPricingEntity);
  };

  useEffect(() => {
    const options = getMinistrySizeOptions(selectedCurriculum.ageGroup);
    setMinistrySizeOptions(options);
    if (selectedOption) {
      const option = options.find(opt => opt.value === selectedOption);
      if (option) {
        handleSelect(option);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurriculum]);

  return (
    <OptionsContainer>
      {ministrySizeOptions.map(option => (
        <Option
          key={option.value}
          isSelected={selectedOption === option.value}
          onClick={() => handleSelect(option)}
          data-qa-hook={`ministry-size-option-${option.value}`}
        >
          <div>
            <OptionTitle>{option.label}</OptionTitle>
            <OptionSubtitle>Attendees</OptionSubtitle>
          </div>
          {currentMinistrySize === option.value && <CurrentTag>Current Subscription</CurrentTag>}
        </Option>
      ))}
    </OptionsContainer>
  );
};

export default SelectMinistrySize;
